import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { FiShare2, FiDownload } from "react-icons/fi"; // Import FiDownload
import { ResturantData } from "@/types/globalTypes";
import { Capacitor } from "@capacitor/core";
import { useTranslation } from "react-i18next";

function QrCode() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [qrCode, setQrCode] = useState<string>("");
  const [error, setError] = useState<string | null>("");
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const menuUrl = useSelector(
      (state: { resturant: { resturantData: ResturantData } }) =>
        state?.resturant?.resturantData?.menuUrl,
    );
  const BaseUrl = import.meta.env.VITE_BASE_URL;
  const url = `${BaseUrl}/api/menu-qrcode?locale=${lang}`;

  useEffect(() => {
    if(!token) return
    const fetchQrCode = async () => {
      try {
        const response = await axios.get(url, {
          responseType: "blob",
          headers: {
            Authorization: token,
          },
        });


        const imageUrl = URL.createObjectURL(response.data);
        setQrCode(imageUrl);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message = error.response?.data?.error || ("qrCode.fetchFailed") as string;
          setError(message);
        } else {
          setError(t("qrCode.fetchFailed") as string);
        }
      }
    };

    fetchQrCode();
  }, [lang, token, t, url,menuUrl]);

  const handleShare = async () => {
    if (Capacitor.getPlatform() !== "web") return
    if (qrCode) {
      try {
        const response = await fetch(qrCode);
        const blob = await response.blob();

        const file = new File([blob], "qrcode.png", { type: "image/png" });
        if (navigator.share) {
          await navigator.share({
            title: "QR Code",
            files: [file],
          });
        } else {
          toast.error(
            ("qrCode.unsupported")  as string,
          );
        }
      } catch (err) {
        console.error("Error sharing QR code:", err);
        toast.error(
          ("qrCode.shareFailed") as string,
        );
      }
    }
  };

  const handleDownload = () => {
    if (Capacitor.getPlatform() !== "web") return
    if (qrCode) {
      const link = document.createElement("a");
      link.href = qrCode;
      link.download = "qrcode.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (error) return null
  if (!qrCode) return null;

  return (
    <div className="mb-[0.75rem] text-center">
      {qrCode && (
        <div
          className="relative mx-auto h-24 w-24"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            src={qrCode}
            alt="QR Code"
            className="h-full w-full rounded-md"
          />
          {/* Overlay with share and download sections */}
          {isHovered && Capacitor.getPlatform() === "web" && (
            <div className="absolute inset-0 flex rounded-md">
              {/* Share Section */}
              <div
                onClick={handleShare}
                className="flex-1 flex items-center justify-center cursor-pointer bg-black bg-opacity-40 hover:bg-opacity-70 rounded-s-md"
              >
                <FiShare2 className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
              {/* Download Section */}
              <div
                onClick={handleDownload}
                className="flex-1 flex items-center justify-center cursor-pointer bg-black bg-opacity-40 hover:bg-opacity-70 rounded-e-md"
              >
                <FiDownload className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default QrCode;